/**
 * QR toggle for the product share dialog.
 * Loaded only when the block's QR setting is on. The generator module is fetched on the first open.
 */

const ready = new WeakMap();

/**
 * @param {HTMLButtonElement} button
 * @param {HTMLElement} panel
 * @param {boolean} expanded
 * @param {HTMLElement} component
 */
function setExpanded(button, panel, expanded, component) {
  button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  button.setAttribute(
    'aria-label',
    expanded ? (component.dataset.hideQrLabel ?? '') : (component.dataset.showQrLabel ?? '')
  );
  panel.hidden = !expanded;
}

/**
 * Shows or hides a QR code for the product URL.
 * @this {HTMLElement & { refs: { qrButton?: HTMLButtonElement, qr?: HTMLElement, status?: HTMLElement }, dataset: DOMStringMap }}
 * @param {Event} event
 */
async function toggleQr(event) {
  const button = event.target instanceof HTMLButtonElement ? event.target : this.refs.qrButton;
  const panel = this.refs.qr;

  if (!(button instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return;

  if (button.getAttribute('aria-expanded') === 'true') {
    setExpanded(button, panel, false, this);
    return;
  }

  if (!ready.get(this)) {
    const url = this.dataset.shareUrl;
    const moduleUrl = this.dataset.qrModule;
    const status = this.refs.status;

    if (!url || !moduleUrl) return;

    try {
      const { QRCode } = await import(moduleUrl);
      panel.replaceChildren();
      new QRCode(panel, {
        text: url,
        width: 168,
        height: 168,
        colorDark: '#121212',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.M,
        useSVG: true,
      });

      const svg = panel.querySelector('svg');
      if (svg) {
        svg.setAttribute('role', 'img');
        svg.setAttribute('aria-label', this.dataset.qrLabel ?? '');
      }

      ready.set(this, true);
    } catch (error) {
      console.error(error);
      if (status instanceof HTMLElement) {
        status.textContent = this.dataset.qrFailedMessage ?? '';
      }
      return;
    }
  }

  setExpanded(button, panel, true, this);
}

/**
 * @param {CustomElementConstructor} ctor
 */
function install(ctor) {
  if ('toggleQr' in ctor.prototype) return;
  ctor.prototype.toggleQr = toggleQr;
}

const defined = customElements.get('product-share-component');
if (defined) {
  install(defined);
} else {
  customElements.whenDefined('product-share-component').then(install);
}
